// src/services/rolePermissions.js
// Centralised role-based permission logic.
// Permissions are stored in localStorage (keyed "roleAccessPermissions") by the
// Role Access tab in TenantSettings.  This module reads that config and exposes
// a simple `can(role, resource, action)` API consumed by every component that
// needs gating.

const ROLES = ['owner', 'admin', 'manager', 'operator'];

const ALL_RESOURCES = [
  { key: 'members',                actions: ['view', 'add', 'update', 'delete'] },
  { key: 'events',                 actions: ['view', 'add', 'update', 'delete'] },
  { key: 'eventItems',             actions: ['view', 'add', 'update', 'delete'] },
  { key: 'eventDetailTask',        actions: ['view', 'add', 'update', 'delete'] },
  { key: 'analytics',              actions: ['view', 'update'] },
  { key: 'posting',                actions: ['view', 'submit', 'schedule', 'update', 'delete'] },
  { key: 'tenantUsers',            actions: ['view', 'add', 'update', 'delete'] },
  { key: 'integrations',           actions: ['view', 'add', 'update', 'delete'] },
  { key: 'tenantSettings',         actions: ['view', 'add', 'update', 'delete'] },
  { key: 'tenantSettingsCompany',   actions: ['view', 'add', 'update', 'delete'] },
  { key: 'tenantSettingsVenmo',     actions: ['view', 'add', 'update', 'delete'] },
  { key: 'tenantSettingsCollab',    actions: ['view', 'add', 'update', 'delete'] },
  { key: 'tenantSettingsRoleAccess', actions: ['view', 'add', 'update', 'delete'] },
];

/** Build the "factory default" permission map (used when nothing is saved yet). */
function buildDefaultPermissions() {
  const perms = {};
  for (const role of ROLES) {
    perms[role] = {};
    for (const res of ALL_RESOURCES) {
      perms[role][res.key] = {};
      for (const action of res.actions) {
        if (role === 'owner' || role === 'admin') {
          perms[role][res.key][action] = true;
        } else if (role === 'manager') {
          const isAdmin = ['tenantUsers', 'integrations'].includes(res.key);
          const isSettings = res.key.startsWith('tenantSettings');
          if (isSettings) {
            perms[role][res.key][action] = action === 'view';
          } else if (isAdmin) {
            perms[role][res.key][action] = ['view', 'add', 'update'].includes(action);
          } else {
            perms[role][res.key][action] = true;
          }
        } else { // operator
          const isCore = ['members', 'events', 'eventItems', 'eventDetailTask'].includes(res.key);
          if (isCore) {
            perms[role][res.key][action] = ['view', 'add', 'update'].includes(action);
          } else if (res.key === 'posting') {
            perms[role][res.key][action] = ['view', 'submit'].includes(action);
          } else {
            perms[role][res.key][action] = action === 'view';
          }
        }
      }
    }
  }
  return perms;
}

/** Read the current permission matrix (falls back to defaults). */
function getPermissions() {
  try {
    const raw = localStorage.getItem('roleAccessPermissions');
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return buildDefaultPermissions();
}

/**
 * Check whether `role` is allowed to perform `action` on `resource`.
 *
 * @param {string} role      - e.g. 'admin', 'manager', 'operator', 'owner'
 * @param {string} resource  - e.g. 'tenantUsers', 'events'
 * @param {string} action    - e.g. 'view', 'add', 'update', 'delete'
 * @returns {boolean}
 */
export function can(role, resource, action) {
  if (!role) return false;
  const r = String(role).toLowerCase();
  // Owner always has full access (safety net)
  if (r === 'owner') return true;
  const perms = getPermissions();
  return !!perms[r]?.[resource]?.[action];
}

/**
 * Convenience: returns an object with boolean flags for a given resource.
 * e.g. permissionsFor('admin', 'tenantUsers') => { view: true, add: true, update: true, delete: true }
 */
export function permissionsFor(role, resource) {
  if (!role) return {};
  const r = String(role).toLowerCase();
  const res = ALL_RESOURCES.find(x => x.key === resource);
  if (!res) return {};
  const out = {};
  for (const action of res.actions) {
    out[action] = can(r, resource, action);
  }
  return out;
}
